/**
 * The dashboard's date range.
 *
 * Boundaries are built from **local date components**, never
 * `toISOString().slice(0, 10)`. The latter converts to UTC first, so on a Prague
 * evening "today" becomes tomorrow and every tile silently reports the wrong day —
 * the same trap the member app's calendar documents.
 *
 * The range lives in the URL rather than in component state, for the reason the
 * protocols library gives for its topic filter: a filtered view is a link somebody
 * sends to a colleague.
 */

export const RANGE_PRESETS = ["7d", "30d", "90d", "custom"] as const;

export type RangePreset = (typeof RANGE_PRESETS)[number];

export const DEFAULT_PRESET: RangePreset = "30d";

/** Matches `analytics_max_report_days` on the API, which rejects anything longer. */
export const MAX_RANGE_DAYS = 92;

const PRESET_DAYS: Record<Exclude<RangePreset, "custom">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export interface DateRange {
  from: string;
  to: string;
}

/** `YYYY-MM-DD` from the *local* calendar components. */
export function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isPreset(value: string | null): value is RangePreset {
  return value !== null && (RANGE_PRESETS as readonly string[]).includes(value);
}

export function isDateKey(value: string | null): value is string {
  return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** The window a preset covers, ending today inclusive. */
export function rangeForPreset(
  preset: Exclude<RangePreset, "custom">,
  today: Date = new Date(),
): DateRange {
  const to = new Date(today);
  const from = new Date(today);
  // Inclusive of both ends, so "7 days" is seven tiles and not eight.
  from.setDate(from.getDate() - (PRESET_DAYS[preset] - 1));
  return { from: toDateKey(from), to: toDateKey(to) };
}

/** Whole days between two keys, inclusive. */
export function rangeLengthDays(range: DateRange): number {
  const from = new Date(`${range.from}T00:00:00`);
  const to = new Date(`${range.to}T00:00:00`);
  return Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
}

export function isValidRange(range: DateRange): boolean {
  if (!isDateKey(range.from) || !isDateKey(range.to)) return false;
  const length = rangeLengthDays(range);
  return length >= 1 && length <= MAX_RANGE_DAYS;
}

/**
 * Resolve what the URL asked for into a usable window.
 *
 * An unusable custom range falls back to the default rather than erroring: a
 * mistyped date in a shared link should still show a dashboard.
 */
export function resolveRange(
  preset: RangePreset,
  custom: Partial<DateRange>,
  today: Date = new Date(),
): DateRange {
  if (preset !== "custom") return rangeForPreset(preset, today);
  const candidate = { from: custom.from ?? "", to: custom.to ?? "" };
  if (!isValidRange(candidate)) return rangeForPreset("30d", today);
  return candidate;
}
